import {
  CalendarDays,
  IndianRupee,
  ShieldCheck,
  WalletCards,
} from "lucide-react";

export type PayoutReportRecord = {
  id: string;
  month: string;
  year: number;
  payoutCount: number;
  receivedReferralIncentive: number;
  tdsPercentage: number;
  tdsAmount: number;
};

interface Props {
  records: PayoutReportRecord[];
  resultStart: number;
  loading?: boolean;
  error?: string | null;
}

const formatAmount = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatCurrency = (value: number) => `₹${formatAmount(value)}`;

const PayoutReportTable = ({
  records,
  resultStart,
  loading,
  error,
}: Props) => {
  return (
    <div className="payout-report-table-card">
      <div
        className="payout-report-table-scroll"
        role="region"
        aria-label="Payout report table"
        tabIndex={0}
      >
        <table className="payout-report-table">
          <colgroup>
            <col className="payout-col-sno" />
            <col className="payout-col-month" />
            <col className="payout-col-order" />
            <col className="payout-col-incentive" />
            <col className="payout-col-status" />
            <col className="payout-col-tds-rate" />
            <col className="payout-col-tds-amount" />
          </colgroup>

          <thead>
            <tr>
              <th>S.NO</th>
              <th>MONTH</th>
              <th>PAYOUTS</th>
              <th>RECEIVED REFERRAL INCENTIVE</th>
              <th>STATUS</th>
              <th>TDS(%)</th>
              <th>TDS AMOUNT</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td className="payout-empty-state" colSpan={7}>
                  Loading payout report...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="payout-empty-state" colSpan={7}>
                  {error}
                </td>
              </tr>
            ) : records.length > 0 ? (
              records.map((record, index) => (
                <tr key={record.id}>
                  <td>
                    <span className="payout-serial">
                      {resultStart + index}
                    </span>
                  </td>

                  <td>
                    <div className="payout-month">
                      <CalendarDays size={15} strokeWidth={2.2} />
                      <span>
                        {record.month} {record.year}
                      </span>
                    </div>
                  </td>

                  <td>
                    <span className="payout-order-no">
                      <WalletCards
                        size={14}
                        style={{ marginRight: 6 }}
                      />
                      {record.payoutCount} payout
                      {record.payoutCount === 1 ? "" : "s"}
                    </span>
                  </td>

                  <td>
                    <div className="payout-incentive">
                      <span className="payout-incentive-icon">
                        <IndianRupee size={15} strokeWidth={2.45} />
                      </span>
                      <div className="payout-incentive-copy">
                        <strong>
                          {formatAmount(
                            record.receivedReferralIncentive,
                          )}
                        </strong>
                        <span>Referral Incentive</span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className="payout-status-badge payout-status-success">
                      Completed
                    </span>
                  </td>

                  <td>
                    <span className="payout-tds-percentage">
                      {record.tdsPercentage}%
                    </span>
                  </td>

                  <td>
                    <div className="payout-tds-value">
                      <span className="payout-tds-icon">
                        <ShieldCheck size={15} strokeWidth={2.3} />
                      </span>
                      <strong>
                        {formatCurrency(record.tdsAmount)}
                      </strong>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="payout-empty-state" colSpan={7}>
                  No payout records found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayoutReportTable;
